import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, CheckCircle2, AlertCircle, ExternalLink, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useEscapeKey } from '../hooks/useEscapeKey';
type Period = 'daily' | 'weekly' | 'monthly';
interface Inspection {
  id: string;
  name: string;
  period: Period;
  required: number;
  completed: number;
  project: string;
  contractor: string;
}
const MOCK_INSPECTIONS: Inspection[] = [
// Daily
{
  id: '1',
  name: 'Daily Site Safety Walk-through',
  period: 'daily',
  required: 7,
  completed: 5,
  project: 'Court Square LIC',
  contractor: 'Broadway Construction Group'
}, {
  id: '2',
  name: 'Daily Equipment Check',
  period: 'daily',
  required: 7,
  completed: 7,
  project: '99 Claremont Ave',
  contractor: 'Broadway Construction Group'
}, {
  id: '3',
  name: 'Daily Scaffolding Inspection',
  period: 'daily',
  required: 7,
  completed: 6,
  project: '251 Douglass Street',
  contractor: 'AV Mechanic LLC'
}, {
  id: '4',
  name: 'Daily Fall Protection Check',
  period: 'daily',
  required: 7,
  completed: 4,
  project: '450 Park Avenue South',
  contractor: 'SteelCo Inc'
}, {
  id: '5',
  name: 'Daily PPE Compliance Check',
  period: 'daily',
  required: 7,
  completed: 7,
  project: 'Court Square LIC',
  contractor: 'Broadway Construction Group'
}, {
  id: '6',
  name: 'Daily Material Storage Review',
  period: 'daily',
  required: 7,
  completed: 3,
  project: '99 Claremont Ave',
  contractor: 'Clear Path Contracting LLC'
},
// Weekly
{
  id: '7',
  name: 'Weekly Fire Extinguisher Inspection',
  period: 'weekly',
  required: 1,
  completed: 1,
  project: 'Court Square LIC',
  contractor: 'Broadway Construction Group'
}, {
  id: '8',
  name: 'Weekly Electrical Systems Check',
  period: 'weekly',
  required: 1,
  completed: 0,
  project: '99 Claremont Ave',
  contractor: 'Clear Path Contracting LLC'
}, {
  id: '9',
  name: 'Weekly Crane Inspection',
  period: 'weekly',
  required: 1,
  completed: 1,
  project: '251 Douglass Street',
  contractor: 'AV Mechanic LLC'
}, {
  id: '10',
  name: 'Weekly First Aid Kit Check',
  period: 'weekly',
  required: 1,
  completed: 1,
  project: '450 Park Avenue South',
  contractor: 'Broadway Construction Group'
}, {
  id: '11',
  name: 'Weekly Emergency Exit Review',
  period: 'weekly',
  required: 1,
  completed: 0,
  project: 'Court Square LIC',
  contractor: 'Broadway Construction Group'
}, {
  id: '12',
  name: 'Weekly Tool Calibration Check',
  period: 'weekly',
  required: 1,
  completed: 1,
  project: '251 Douglass Street',
  contractor: 'SteelCo Inc'
},
// Monthly
{
  id: '13',
  name: 'Monthly Environmental Compliance Audit',
  period: 'monthly',
  required: 1,
  completed: 1,
  project: 'Court Square LIC',
  contractor: 'Broadway Construction Group'
}, {
  id: '14',
  name: 'Monthly Structural Integrity Review',
  period: 'monthly',
  required: 1,
  completed: 0,
  project: '99 Claremont Ave',
  contractor: 'SteelCo Inc'
}, {
  id: '15',
  name: 'Monthly OSHA Compliance Check',
  period: 'monthly',
  required: 1,
  completed: 1,
  project: '251 Douglass Street',
  contractor: 'Broadway Construction Group'
}, {
  id: '16',
  name: 'Monthly Safety Training Records',
  period: 'monthly',
  required: 1,
  completed: 1,
  project: '450 Park Avenue South',
  contractor: 'AV Mechanic LLC'
}];
const PROJECTS = ['All Projects', 'Court Square LIC', '99 Claremont Ave', '251 Douglass Street', '450 Park Avenue South'];
const CONTRACTORS = ['All Contractors', 'Broadway Construction Group', 'AV Mechanic LLC', 'SteelCo Inc', 'Clear Path Contracting LLC'];
interface InspectionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function InspectionsModal({
  isOpen,
  onClose
}: InspectionsModalProps) {
  const [activeTab, setActiveTab] = useState<Period>('daily');
  const [selectedProject, setSelectedProject] = useState('All Projects');
  const [selectedContractor, setSelectedContractor] = useState('All Contractors');
  useEscapeKey(onClose, isOpen);
  // Filter inspections by period, project, and contractor
  const filteredInspections = MOCK_INSPECTIONS.filter(inspection => {
    if (inspection.period !== activeTab) return false;
    if (selectedProject !== 'All Projects' && inspection.project !== selectedProject) {
      return false;
    }
    if (selectedContractor !== 'All Contractors' && inspection.contractor !== selectedContractor) {
      return false;
    }
    return true;
  });
  const calculateStats = (period: Period) => {
    const inspections = MOCK_INSPECTIONS.filter(i => {
      if (i.period !== period) return false;
      if (selectedProject !== 'All Projects' && i.project !== selectedProject) {
        return false;
      }
      if (selectedContractor !== 'All Contractors' && i.contractor !== selectedContractor) {
        return false;
      }
      return true;
    });
    const totalRequired = inspections.reduce((sum, i) => sum + i.required, 0);
    const totalCompleted = inspections.reduce((sum, i) => sum + i.completed, 0);
    return {
      totalRequired,
      totalCompleted
    };
  };
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-743">
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-744">
        {/* Backdrop */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-black/40 backdrop-blur-sm" data-id="element-745" />

        {/* Modal */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        duration: 0.5,
        bounce: 0.3
      }} className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col" data-id="element-746">
          {/* Header */}
          <div className="p-6 border-b border-gray-200" data-id="element-747">
            <div className="flex items-center justify-between mb-4" data-id="element-748">
              <div data-id="element-749">
                <h2 className="text-2xl font-bold text-gray-900" data-id="element-750">
                  Inspections & Logs
                </h2>
                <p className="text-sm text-gray-600 mt-1" data-id="element-751">
                  Quick overview of inspection status
                </p>
              </div>
              <div className="flex items-center gap-2" data-id="element-752">
                <Link to="/inspections" className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors" data-id="element-753">
                  Full View
                  <ExternalLink size={14} data-id="element-754" />
                </Link>
                <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-755">
                  <X size={20} data-id="element-756" />
                </button>
              </div>
            </div>

            {/* Filters */}
            <div className="flex gap-3" data-id="element-757">
              {/* Project Filter */}
              <div className="flex-1 relative" data-id="element-758">
                <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 pr-8 text-sm text-gray-900 appearance-none focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent cursor-pointer" data-id="element-759">
                  {PROJECTS.map(project => <option key={project} value={project} data-id="element-760">
                      {project}
                    </option>)}
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-761" />
              </div>

              {/* Contractor Filter */}
              <div className="flex-1 relative" data-id="element-762">
                <select value={selectedContractor} onChange={e => setSelectedContractor(e.target.value)} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 pr-8 text-sm text-gray-900 appearance-none focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent cursor-pointer" data-id="element-763">
                  {CONTRACTORS.map(contractor => <option key={contractor} value={contractor} data-id="element-764">
                      {contractor}
                    </option>)}
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-765" />
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 px-6" data-id="element-766">
            {(['daily', 'weekly', 'monthly'] as Period[]).map(period => {
            const stats = calculateStats(period);
            const isActive = activeTab === period;
            const periodLabel = period.charAt(0).toUpperCase() + period.slice(1);
            return <button key={period} onClick={() => setActiveTab(period)} className={`
                    relative px-6 py-4 text-sm font-medium transition-colors
                    ${isActive ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'}
                  `} data-id="element-767">
                  <div className="flex items-center gap-3" data-id="element-768">
                    <span data-id="element-769">{periodLabel} Inspections</span>
                    <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full" data-id="element-770">
                      {stats.totalCompleted}/{stats.totalRequired}
                    </span>
                  </div>
                  {isActive && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900" transition={{
                type: 'spring',
                duration: 0.5,
                bounce: 0.2
              }} data-id="element-771" />}
                </button>;
          })}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6" data-id="element-772">
            {filteredInspections.length === 0 ? <div className="flex flex-col items-center justify-center py-12 text-center" data-id="element-773">
                <Calendar size={48} className="text-gray-300 mb-3" data-id="element-774" />
                <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-775">
                  No inspections found
                </h3>
                <p className="text-sm text-gray-600" data-id="element-776">
                  Try adjusting your filters to see more results
                </p>
              </div> : <motion.div key={`${activeTab}-${selectedProject}-${selectedContractor}`} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.2
          }} className="grid grid-cols-2 gap-3" data-id="element-777">
                {filteredInspections.map((inspection, index) => {
              const completionRate = inspection.completed / inspection.required * 100;
              const isComplete = inspection.completed >= inspection.required;
              const isPartial = inspection.completed > 0 && !isComplete;
              return <motion.div key={inspection.id} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: index * 0.03,
                duration: 0.2
              }} className="border border-gray-200 rounded-lg p-3 hover:border-gray-300 transition-colors" data-id="element-778">
                      <div className="flex items-start justify-between mb-2" data-id="element-779">
                        <h4 className="text-sm font-semibold text-gray-900 leading-tight flex-1" data-id="element-780">
                          {inspection.name}
                        </h4>
                        <div className={`ml-2 flex-shrink-0 ${isComplete ? 'text-emerald-600' : isPartial ? 'text-amber-600' : 'text-red-600'}`} data-id="element-781">
                          {isComplete ? <CheckCircle2 size={18} data-id="element-782" /> : <AlertCircle size={18} data-id="element-783" />}
                        </div>
                      </div>

                      <div className="flex items-center justify-between mb-2" data-id="element-784">
                        <span className="text-xs text-gray-600" data-id="element-785">
                          {inspection.completed} of {inspection.required}
                        </span>
                        <span className={`text-xs font-bold ${isComplete ? 'text-emerald-600' : isPartial ? 'text-amber-600' : 'text-red-600'}`} data-id="element-786">
                          {Math.round(completionRate)}%
                        </span>
                      </div>

                      {/* Progress bar */}
                      <div className="relative h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-787">
                        <motion.div initial={{
                    width: 0
                  }} animate={{
                    width: `${completionRate}%`
                  }} transition={{
                    duration: 0.5,
                    ease: 'easeOut',
                    delay: index * 0.03
                  }} className={`absolute top-0 left-0 h-full ${isComplete ? 'bg-emerald-500' : isPartial ? 'bg-amber-500' : 'bg-red-500'}`} data-id="element-788" />
                      </div>
                    </motion.div>;
            })}
              </motion.div>}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
}